import React, { useContext, useEffect, useCallback, useState } from "react";
import {
  setAuthFunctions,
  setAuthData,
  getAuthData,
  isTokenExpired,
  updateToken,
} from "@/api/service";
import { login, logout } from "@/api/backend/auth";
import { UserType } from "@/types/user";
import { useCartStore } from "@/stores/cartStore";
interface IAuthContext {
  signin: (data: UserType) => Promise<void>;
  signout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = React.createContext({} as IAuthContext);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { initiateCartSession } = useCartStore();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const signin = useCallback(async (data: UserType) => {
    try {
      const { username, password } = data;
      const loginResponse = await login({
        username,
        password,
      });
      if (!loginResponse) {
        throw new Error("Failed to login");
      }

      if (loginResponse?.status === "error") {
        const message = loginResponse?.error?.message || "Unknown login error.";
        throw new Error(message);
      }

      // Check if the response contains an error
      if (loginResponse?.data?.error) {
        throw new Error(loginResponse.data.error);
      }

      // Set the access token in the store
      if (!loginResponse.accessToken) {
        throw new Error("No access token in login response");
      }
      const { accessToken, refreshToken } = loginResponse;
      if (refreshToken) {
        setAuthData({
          accessToken,
          refreshToken,
        });
        setIsAuthenticated(checkAuthentication());
      }
    } catch (err: any) {
      console.log("Error signing in", err);
      // Normalize known Axios-style error responses
      if (err?.response?.data?.message) {
        throw new Error(err.response.data.message);
      }

      if (err?.message) {
        throw new Error(err.message);
      }

      throw new Error("Unexpected error occurred during sign in.");
    }
  }, []);

  const signout = useCallback(async () => {
    await logout();
    // Clear the access token and refresh token from services.ts
    setAuthData({
      accessToken: null,
      refreshToken: null,
    });
    // Clear refresh token from local storage
    localStorage.removeItem("on-demand-refresh-token");

    // Set isAuthenticated to false
    setIsAuthenticated(checkAuthentication());
  }, []);

  const checkAuthentication = () => {
    // check if the access token exists
    const { accessToken } = getAuthData() || {};
    if (!accessToken) return false;
    // check if the token is expired
    if (isTokenExpired(accessToken)) return false;

    return true;
  };

  // Check if user is authenticated
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      // On Remount we want the refresh token from local storage to get the acccess token and set it in the store.

      try {
        const refreshToken = localStorage.getItem("on-demand-refresh-token");
        if (refreshToken) {
          setAuthData({ refreshToken });

          await updateToken();
          // Revalidate Cart and Cart Token
          initiateCartSession();
        }
      } catch (error) {
        console.log(error);
      }

      setIsAuthenticated(checkAuthentication());
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    signin,
    signout,
    isAuthenticated,
    isLoading,
  };

  useEffect(() => {
    setAuthFunctions({ signout });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   setAuthData({ accessToken, refreshToken });
  // }, [accessToken, refreshToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
